import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Users, Handshake, Repeat, Hourglass, X, Building2, FileText, CalendarDays } from "lucide-react";
import { SmmAccessGate } from "@/components/crm/SmmAccessGate";
import { getWorkplaceReports, getWorkplaceUserDetail, type WorkplaceUserDetail } from "@/lib/smm-access.functions";

export const Route = createFileRoute("/crm/smm/reports")({
  component: () => (
    <SmmAccessGate>{(access) => <ReportsPage access={access} />}</SmmAccessGate>
  ),
});

function fmtEUR(n: number) {
  return `€${Math.round(n).toLocaleString("en-US")}`;
}

export function ReportsPage({ access }: { access: { isSmmAdmin: boolean; userId: string } }) {
  const fn = useServerFn(getWorkplaceReports);
  const [selected, setSelected] = useState<string | "all">("all");
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["smm-reports", access.isSmmAdmin ? selected : access.userId],
    queryFn: () =>
      fn({
        data:
          access.isSmmAdmin && selected !== "all"
            ? { user_id: selected }
            : access.isSmmAdmin
              ? {}
              : { user_id: access.userId },
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-3 text-[#212529]">
      {access.isSmmAdmin && (
        <div className="flex justify-end">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value as any)}
            className="h-8 px-2.5 rounded-lg border text-xs bg-white"
            style={{ borderColor: "#dee2e6" }}
          >
            <option value="all">All Workplace users</option>
            {reports.map((r) => (
              <option key={r.user_id} value={r.user_id}>
                {r.display_name || r.email || r.user_id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="p-8 text-center text-sm text-[#6c757d]">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-[#6c757d]" style={{ borderColor: "#eceff3" }}>
          No data yet.
        </div>
      ) : (
        <ul className="grid gap-2.5 md:grid-cols-2">
          {reports.map((r) => (
            <li key={r.user_id}>
              <button
                type="button"
                onClick={() => setOpenUserId(r.user_id)}
                className="w-full text-left bg-white border rounded-xl p-3.5 hover:border-[#9ec5fe] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                style={{ borderColor: "#eceff3" }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#212529] truncate">
                      {r.display_name || r.email || r.user_id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-[#6c757d] truncate">{r.email}</div>
                  </div>
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0"
                    style={{
                      background: r.role === "smm_admin" ? "#e7f1ff" : "#f8f9fa",
                      color: r.role === "smm_admin" ? "#0d6efd" : "#6c757d",
                    }}
                  >
                    {r.role === "smm_admin" ? "Admin" : "Workplace"}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Metric icon={Users} tint="#0d6efd" label="Clients" value={r.clients_count} />
                  <Metric icon={Repeat} tint="#0dcaf0" label="Active subs" value={r.active_subscriptions} />
                  <Metric icon={Handshake} tint="#198754" label="Deals won" value={r.deals_won} />
                  <Metric icon={Hourglass} tint="#fd7e14" label="Pending" value={r.deals_pending} />
                </div>
                <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3 text-sm" style={{ borderColor: "#eceff3" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#6c757d] font-semibold">Pipeline</div>
                    <div className="font-bold text-[#212529] tabular-nums">{fmtEUR(r.pipeline_value_eur)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#6c757d] font-semibold">Won</div>
                    <div className="font-bold text-[#198754] tabular-nums">{fmtEUR(r.won_value_eur)}</div>
                  </div>
                </div>
                <div className="mt-2 text-[11px] font-semibold text-[#0d6efd]">View details →</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {openUserId && (
        <DetailDrawer userId={openUserId} onClose={() => setOpenUserId(null)} />
      )}
    </div>
  );
}

function Metric({
  icon: Icon,
  tint,
  label,
  value,
}: {
  icon: typeof Users;
  tint: string;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-[#f8f9fb]">
      <div className="size-7 grid place-items-center rounded-md bg-white shrink-0" style={{ color: tint }}>
        <Icon className="size-3.5" strokeWidth={1.85} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-[#6c757d] font-semibold truncate">{label}</div>
        <div className="text-sm font-bold text-[#212529] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function DetailDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const fn = useServerFn(getWorkplaceUserDetail);
  const { data, isLoading } = useQuery<WorkplaceUserDetail>({
    queryKey: ["smm-user-detail", userId],
    queryFn: () => fn({ data: { user_id: userId } }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-0 md:p-6" onClick={onClose}>
      <div
        className="bg-white w-full md:max-w-3xl md:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#eceef5]">
          <div>
            <div className="text-sm font-bold text-[#0a0a14]">
              {data?.user.display_name || data?.user.email || "Workplace User"}
            </div>
            <div className="text-xs text-[#6b7280]">{data?.user.email}</div>
          </div>
          <button onClick={onClose} className="size-9 grid place-items-center rounded-full hover:bg-[#f6f7fb]" aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5 space-y-6">
          {isLoading || !data ? (
            <div className="py-10 text-center text-sm text-[#6b7280]">Loading...</div>
          ) : (
            <>
              <Section icon={<Building2 className="size-4" />} title="Clients" count={data.clients.length}>
                {data.clients.length === 0 ? (
                  <Empty label="No clients assigned." />
                ) : (
                  <ul className="divide-y divide-[#eceef5] border border-[#eceef5] rounded-xl overflow-hidden">
                    {data.clients.map((c) => (
                      <li key={c.id} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div>
                          <div className="font-semibold text-[#0a0a14]">{c.company_name || c.contact_name || c.id.slice(0, 8)}</div>
                          {c.contact_name && <div className="text-xs text-[#6b7280]">{c.contact_name}</div>}
                        </div>
                        {c.status && <span className="text-[10px] uppercase font-bold tracking-wider text-[#0d6efd] bg-[#e7f1ff] px-2 py-0.5 rounded-full">{c.status}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={<FileText className="size-4" />} title="Deals" count={data.deals.length}>
                {data.deals.length === 0 ? (
                  <Empty label="No deals recorded." />
                ) : (
                  <ul className="divide-y divide-[#eceef5] border border-[#eceef5] rounded-xl overflow-hidden">
                    {data.deals.map((d) => (
                      <li key={d.id} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0a0a14] truncate">{d.title || d.client_name || "Untitled deal"}</div>
                          <div className="text-xs text-[#6b7280] truncate">{d.client_name}</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full"
                            style={{
                              background: d.status === "won" ? "#dcfce7" : d.status === "lost" ? "#fee2e2" : "#fef3c7",
                              color: d.status === "won" ? "#166534" : d.status === "lost" ? "#991b1b" : "#92400e",
                            }}>{d.status}</span>
                          <span className="font-bold text-[#0a0a14]">{fmtEUR(Number(d.value_eur ?? 0))}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={<CalendarDays className="size-4" />} title="Subscriptions" count={data.subscriptions.length}>
                {data.subscriptions.length === 0 ? (
                  <Empty label="No subscriptions." />
                ) : (
                  <ul className="divide-y divide-[#eceef5] border border-[#eceef5] rounded-xl overflow-hidden">
                    {data.subscriptions.map((s) => (
                      <li key={s.id} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0a0a14] truncate">{s.client_name || s.client_id.slice(0, 8)}</div>
                          <div className="text-xs text-[#6b7280]">
                            {s.starts_on ?? "—"} → {s.ends_on ?? "—"}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full"
                          style={{
                            background: s.status === "active" ? "#dcfce7" : "#f1f5f9",
                            color: s.status === "active" ? "#166534" : "#475569",
                          }}>{s.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, count, children }: { icon: React.ReactNode; title: string; count: number; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <div className="size-7 grid place-items-center rounded-md bg-[#e7f1ff] text-[#0d6efd]">{icon}</div>
        <h2 className="text-sm font-bold text-[#0a0a14]">{title}</h2>
        <span className="text-[11px] font-bold text-[#6b7280]">{count}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="border border-dashed border-[#eceef5] rounded-xl p-6 text-center text-sm text-[#6b7280]">{label}</div>;
}
